import { motion } from "framer-motion";

const NODES = [
    "Manufacturers",
    "Suppliers",
    "Logistics Partners",
    "Warehousing Partners",
    "Distributors",
    "Retailers",
    "Consumers",
];

const STEPS = [
    {
        n: "01",
        title: "Strategic Sourcing",
        body: "Supplier identification and qualification, procurement management and supply chain optimization.",
    },
    {
        n: "02",
        title: "Logistics Coordination",
        body: "Freight management, inventory planning, warehousing partnerships and distribution support.",
    },
    {
        n: "03",
        title: "Trade Compliance Support",
        body: "Customs documentation coordination, import-export support and cross-border movement readiness.",
    },
];

export const SupplyChain = () => {
    return (
        <section
            id="supply-chain"
            data-testid="supply-chain-section"
            className="relative bg-[#0B0807]/82 backdrop-blur-md py-24 sm:py-32 lg:py-40 px-4 sm:px-8 lg:px-12 border-t border-white/5"
        >
            <div className="flex items-center gap-3 font-mono-text text-[10px] sm:text-xs uppercase tracking-[0.3em] text-zinc-500 mb-6">
                <span className="h-px w-10 bg-[#D69331]" />
                <span>06 / Supply Chain Network</span>
            </div>

            <motion.h2
                initial={{ y: 60, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8 }}
                className="font-display uppercase font-bold leading-[0.9] tracking-tight text-white text-4xl sm:text-6xl lg:text-7xl max-w-5xl"
            >
                Building efficient connections{" "}
                <span className="text-zinc-600">across global markets.</span>
            </motion.h2>

            <p className="mt-8 font-mono-text text-sm sm:text-base text-zinc-400 leading-relaxed max-w-3xl">
                Modern international trade depends on efficient, transparent
                and reliable supply chain management — coordinating multiple
                stakeholders, ensuring compliance, maintaining product
                integrity and optimizing costs.
            </p>

            {/* Flow chain */}
            <div className="mt-16 relative">
                <div className="overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0">
                    <div className="flex items-stretch gap-px min-w-max">
                        {NODES.map((node, i) => (
                            <motion.div
                                key={node}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{
                                    duration: 0.5,
                                    delay: i * 0.08,
                                }}
                                className="flex items-center"
                            >
                                <div className="px-5 sm:px-8 py-5 sm:py-6 bg-[#0a0a0a] border border-white/10 min-w-[180px] sm:min-w-[220px]">
                                    <span className="block font-mono-text text-[10px] uppercase tracking-[0.3em] text-zinc-600 mb-2">
                                        Node {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="block font-display uppercase text-base sm:text-lg lg:text-xl text-white">
                                        {node}
                                    </span>
                                </div>
                                {i < NODES.length - 1 && (
                                    <span className="text-[#D69331] px-3 font-mono-text">
                                        →
                                    </span>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Steps */}
            <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
                {STEPS.map((s, i) => (
                    <motion.div
                        key={s.n}
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ duration: 0.6, delay: i * 0.1 }}
                        className="bg-[#0a0a0a] hover:bg-[#0f0f0f] transition-colors p-8 sm:p-10 lg:p-12 group min-h-[280px] flex flex-col"
                    >
                        <span className="font-display text-5xl sm:text-6xl font-bold text-zinc-800 group-hover:text-[#D69331] transition-colors">
                            {s.n}
                        </span>
                        <h3 className="mt-6 font-display uppercase text-xl sm:text-2xl lg:text-3xl font-semibold leading-tight text-white">
                            {s.title}
                        </h3>
                        <p className="mt-4 font-mono-text text-sm text-zinc-400 leading-relaxed">
                            {s.body}
                        </p>
                    </motion.div>
                ))}
            </div>
        </section>
    );
};
